<script setup>
import { ref, computed, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { updateUserCompanyDetails } from '@/modules/admin/users/api/users.service'
import { useAppToast } from '@/shared/lib/useAppToast'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  selectedUser: {
    type: Object,
    default: null
  },
  roles: {
    type: Array,
    default: () => []
  },
  permissions: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  canEditCompanyDetails: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'updated'])

const toast = useAppToast()

const editingCompany = ref(false)
const editingBin = ref(false)
const savingCompany = ref(false)
const savingBin = ref(false)
const companyInput = ref('')
const binInput = ref('')
const companyError = ref('')
const binError = ref('')

watch(() => props.selectedUser, (user) => {
  companyInput.value = user?.companyName ?? ''
  binInput.value = user?.bin ?? ''
  editingCompany.value = false
  editingBin.value = false
  companyError.value = ''
  binError.value = ''
}, { immediate: true })

function closeDialog() {
  if (props.loading) return
  emit('close')
}

function startEditCompany() {
  companyError.value = ''
  editingCompany.value = true
}

function startEditBin() {
  binError.value = ''
  editingBin.value = true
}

async function saveCompany() {
  companyError.value = ''
  if (!companyInput.value.trim()) {
    companyError.value = 'Введите название компании'
    return
  }
  savingCompany.value = true
  try {
    await updateUserCompanyDetails(props.selectedUser.id, { companyName: companyInput.value.trim() })
    emit('updated', { companyName: companyInput.value.trim() })
    toast.success('Название компании обновлено')
    editingCompany.value = false
  } catch (error) {
    companyError.value = error.message || 'Ошибка при сохранении'
  } finally {
    savingCompany.value = false
  }
}

async function saveBin() {
  binError.value = ''
  if (!binInput.value.trim()) {
    binError.value = 'Введите БИН'
    return
  }
  if (!/^\d{12}$/.test(binInput.value.trim())) {
    binError.value = 'БИН должен содержать ровно 12 цифр'
    return
  }
  savingBin.value = true
  try {
    await updateUserCompanyDetails(props.selectedUser.id, { bin: binInput.value.trim() })
    emit('updated', { bin: binInput.value.trim() })
    toast.success('БИН обновлён')
    editingBin.value = false
  } catch (error) {
    binError.value = error.message || 'Ошибка при сохранении'
  } finally {
    savingBin.value = false
  }
}

const formattedCreatedAt = computed(() => {
  if (!props.selectedUser?.createdAt) return '—'

  const date = new Date(props.selectedUser.createdAt)
  if (isNaN(date.getTime())) return props.selectedUser.createdAt

  return date.toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
})

const hasRoles = computed(() => props.roles?.length > 0)
const hasPermissions = computed(() => props.permissions?.length > 0)
</script>

<template>
  <Dialog
    :visible="props.visible"
    modal
    header="Детали пользователя"
    :style="{ width: '640px' }"
    class="user-dialog"
    @update:visible="closeDialog"
    :closable="!props.loading"
  >
    <div v-if="props.loading" class="state-block">
      Загрузка...
    </div>

    <div v-if="props.selectedUser" class="user-details">

      <div class="info-grid">
        <div class="info-item">
          <span class="label">ID</span>
          <span class="value">{{ props.selectedUser.id }}</span>
        </div>

        <div class="info-item">
          <span class="label">Email</span>
          <span class="value">{{ props.selectedUser.email || '—' }}</span>
        </div>

        <div class="info-item">
          <span class="label">ФИО</span>
          <span class="value">{{ props.selectedUser.fullName || '—' }}</span>
        </div>

        <div class="info-item">
          <span class="label">Телефон</span>
          <span class="value">{{ props.selectedUser.phoneNumber || '—' }}</span>
        </div>

        <div class="info-item">
          <span class="label">Тип клиента</span>
          <span class="value">{{ props.selectedUser.clientType || '—' }}</span>
        </div>

        <div class="info-item">
          <span class="label">Компания</span>

          <div class="value value--action">
            <template v-if="!editingCompany">
              <span>{{ props.selectedUser.companyName || '—' }}</span>

              <Button
                v-if="props.canEditCompanyDetails"
                label="Изменить"
                size="small"
                class="edit-btn"
                outlined
                @click="startEditCompany"
              />
            </template>

            <div v-else class="input-block">
              <InputText v-model="companyInput" placeholder="Название компании" />
              <Button label="Сохранить" size="small" :loading="savingCompany" @click="saveCompany" />
              <small v-if="companyError" class="field-error">{{ companyError }}</small>
            </div>
          </div>
        </div>

        <div class="info-item">
          <span class="label">БИН</span>

          <div class="value value--action">
            <template v-if="!editingBin">
              <span>{{ props.selectedUser.bin || '—' }}</span>

              <Button
                v-if="props.canEditCompanyDetails"
                label="Изменить"
                size="small"
                class="edit-btn"
                outlined
                @click="startEditBin"
              />
            </template>

            <div v-else class="input-block">
              <InputText v-model="binInput" placeholder="123456789012" />
              <Button label="Сохранить" size="small" :loading="savingBin" @click="saveBin" />
              <small v-if="binError" class="field-error">{{ binError }}</small>
            </div>
          </div>
        </div>

        <div class="info-item">
          <span class="label">Активен</span>
          <span class="value">
            {{ props.selectedUser.isActive ? 'Да' : 'Нет' }}
          </span>
        </div>

        <div class="info-item">
          <span class="label">Email подтверждён</span>
          <span class="value">
            {{ props.selectedUser.emailConfirmed ? 'Да' : 'Нет' }}
          </span>
        </div>

        <div class="info-item">
          <span class="label">Создан</span>
          <span class="value">{{ formattedCreatedAt }}</span>
        </div>
      </div>

      <div class="block">
        <div class="block-title">Роли</div>

        <div v-if="hasRoles" class="chips">
          <span
            v-for="role in props.roles"
            :key="role.id ?? role.name ?? role"
            class="chip"
          >
            {{ role.name ?? role }}
          </span>
        </div>

        <div v-else class="empty">—</div>
      </div>

      <div class="block">
        <div class="block-title">Права</div>

        <div v-if="hasPermissions" class="chips">
          <span
            v-for="permission in props.permissions"
            :key="permission.id ?? permission.name ?? permission"
            class="chip chip--light"
          >
            {{ permission.name ?? permission }}
          </span>
        </div>

        <div v-else class="empty">—</div>
      </div>

    </div>

    <template #footer>
      <Button
        label="Закрыть"
        icon="pi pi-times"
        class="close-btn"
        outlined
        :disabled="props.loading"
        @click="closeDialog"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.user-details {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  border: 1px solid #eeeeee;
  border-radius: 14px;
  background: #fafafa;
}

.label {
  font-size: 12px;
  font-weight: 600;
  color: #777;
}

.value {
  font-size: 15px;
  font-weight: 500;
  color: #111;
  word-break: break-word;
}

.value--action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.input-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.field-error {
  color: #c41217;
  font-size: 0.82rem;
}

.block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.block-title {
  font-size: 14px;
  font-weight: 700;
  color: #111;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  padding: 6px 12px;
  border-radius: 999px;
  background: #111;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
}

.chip--light {
  background: #f6f6f6;
  color: #333;
  border: 1px solid #e5e5e5;
}

.empty {
  color: #999;
  font-size: 14px;
}

:deep(.edit-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
  white-space: nowrap;
  flex-shrink: 0;
}

:deep(.edit-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.close-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.close-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.close-btn.p-button .p-button-icon) {
  color: #c41217;
}

@media (max-width: 640px) {
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>